import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import '../styles/deposit-modal.css'
import { apiErrorMessage } from '../utils/apiErrorMessage'

/**
 * TransactionModal Component
 *
 * Deposit / Withdraw form. Behaviour dexch ke CreditActivityModal > CreditForm
 * se aligned hai:
 *   - upar dono taraf ke balance: current | naya (projected) — amount type karte
 *     hi live update hota hai. deposit: parent ghata, user badha | withdraw: ulta
 *   - validation inline hoti hai (toast nahi), modal request ke dauraan khula
 *     rehta hai aur submit button busy rehta hai
 *   - modal SIRF success pe band hota hai; fail pe error form me dikhta hai
 *     taaki bheji hui values dobara type na karni pade
 *
 * @param {boolean}  props.show    - modal visible hai ya nahi
 * @param {Function} props.onHide  - modal band karne ka callback
 * @param {Object}   props.userData    - target account (list row)
 * @param {Object}   props.currentUser - logged-in account (optional override)
 * @param {'deposit'|'withdraw'} props.mode
 * @param {Function} props.onSubmit - async, API result return kare ({ success, message })
 * @param {string}   props.title
 * @param {string}   props.targetField - receiver ka konsa number hilta hai:
 *   'inr_balance'       → Account List ka D/W (wallet deposit/withdraw API)
 *   'settlement_points' → settlement API wala flow
 *   'credit'            → Credit (C) action
 * @param {boolean}  props.requireRemark - remark mandatory hai ya nahi. Sirf
 *   /admin/wallet/withdraw isko force karta hai; settlement API nahi.
 */
function TransactionModal({
  show,
  onHide,
  userData = null,
  currentUser = null,
  mode = 'deposit',
  onSubmit,
  title = "Transaction",
  targetField = 'credit',
  requireRemark = null,
}) {
  const authUser = useSelector((state) => state.auth.user)
  const [formData, setFormData] = useState({
    amount: '',
    remark: '',
    transactionPassword: ''
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const isWithdraw = mode === 'withdraw'

  // Modal parent me hamesha mounted rehta hai, isliye har open pe form saaf karo
  // (warna pichhli koshish ka amount/remark/password bacha reh jaata hai).
  useEffect(() => {
    if (!show) return
    setFormData({ amount: '', remark: '', transactionPassword: '' })
    setError('')
    setBusy(false)
  }, [show])

  const num = (v) => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }
  const fmt = (n) => (Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '')

  // Sender ka wahi balance dikhao jo server actually hilaata hai: walletService
  // addDeposit/withdrawFromChild sender ke `inr_balance` par kaam karte hain.
  const parent = currentUser || authUser
  const parentName = parent?.username || 'Sender'
  const parentBal = num(parent?.inr_balance ?? parent?.wallet?.inr_balance ?? parent?.credit ?? parent?.available_points)

  // Receiver ki taraf jo number actually hilta hai wahi dikhao, taaki submit ke
  // baad row aur modal same figure dikhaayein:
  //   D / W      → available_balance (Account List ka "Available Balance" column:
  //                USER → cash, staff → inr_balance − pl_point; wahi jo server
  //                withdraw me check karta hai)
  //   credit (C) → credit, yaani list ka "Credit Reference" column
  const userName = userData?.username || 'Receiver'
  const userBal = targetField === 'settlement_points'
    ? num(userData?.settlement_points)
    : targetField === 'inr_balance'
      ? num(userData?.available_balance ?? userData?.inr_balance)
      : num(userData?.credit ?? userData?.credit_ref ?? userData?.cash_received)

  const amt = num(formData.amount)
  // deposit: parent se nikalta hai, user me jaata hai | withdraw: ulta
  const parentNew = isWithdraw ? parentBal + amt : parentBal - amt
  const userNew = isWithdraw ? userBal - amt : userBal + amt

  // Right-hand boxes — reference's calculateDipositeChange / calculateWithdrawChange:
  //   amount > 0 → the new balances, toFixed(2)
  //   otherwise  → sender box repeats his current balance, receiver box shows 0
  // so the modal opens PREFILLED (e.g. 7845.2 | 7845.2 and 716.3 | 0), never blank.
  const parentNewText = amt > 0 ? parentNew.toFixed(2) : fmt(parentBal)
  const userNewText = amt > 0 ? userNew.toFixed(2) : '0'

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!amt || amt <= 0) {
      setError('Enter a valid amount')
      return
    }
    // /admin/wallet/withdraw remark ko mandatory maanta hai (deposit pe optional).
    // Settlement API remark maangta hi nahi, isliye caller override kar sakta hai.
    const remarkNeeded = requireRemark == null ? isWithdraw : requireRemark
    if (remarkNeeded && !formData.remark.trim()) {
      setError('Remark is required')
      return
    }
    if (String(formData.remark).trim().length > 200) {
      setError('Remark max 200 characters allowed')
      return
    }
    if (!formData.transactionPassword) {
      setError('Transaction Password required')
      return
    }

    setBusy(true)
    try {
      const res = await onSubmit?.(formData)
      // onSubmit API ka response lautata hai — success pe hi band karo
      if (res?.success) {
        setFormData({ amount: '', remark: '', transactionPassword: '' })
        onHide()
      } else {
        setError(res?.message || res?.error || 'Something went wrong')
      }
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    if (busy) return
    setFormData({
      amount: '',
      remark: '',
      transactionPassword: ''
    })
    setError('')
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-md modal-dialog-scrollable"
    >
      {/* reference renders <header class="modal-header"> */}
      <header className="modal-header">
        {/* reference: Deposit = <h4 class="modal-title">, Withdraw = <h5 class="modal-title text-dark"> */}
        <Modal.Title as={isWithdraw ? 'h5' : 'h4'} className={isWithdraw ? 'text-dark' : undefined}>{title}</Modal.Title>
        <button
          type="button"
          className="close text-white"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <Modal.Body>
        <form
          onSubmit={handleSubmit}
          data-vv-scope="usertransactionMDL"
          method="post"
          noValidate
        >
          {/* Sender (logged-in account): current balance | transaction ke baad */}
          <div className="form-group row">
            <label className="col-form-label col-4">{parentName}</label>
            <div className="col-8">
              <div className="row">
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={fmt(parentBal)}
                  />
                </div>
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={parentNewText}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Receiver (target account): current balance | transaction ke baad */}
          <div className="form-group row">
            <label className="col-form-label col-4">{userName}</label>
            <div className="col-8">
              <div className="row">
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={fmt(userBal)}
                  />
                </div>
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={userNewText}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Amount</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                type="number"
                name="amount"
                className="form-control txt-right"
                value={formData.amount}
                onChange={handleChange}
                step="any"
                min="0"
                aria-required="true"
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Remark</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <textarea
                name="remark"
                className="form-control"
                value={formData.remark}
                onChange={handleChange}
                maxLength={200}
                aria-required="true"
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Transaction Password</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                name="transactionPassword"
                type="password"
                className="form-control"
                value={formData.transactionPassword}
                onChange={handleChange}
                aria-required="true"
              />
            </div>
          </div>

          {error && (
            <div className="form-group row">
              <div className="col-12 text-right text-danger">{error}</div>
            </div>
          )}

          <div className="form-group row">
            <div className="col-12 text-right">
              <button
                type="button"
                className="btn btn-back"
                onClick={handleClose}
                disabled={busy}
              >
                <i className="fas fa-undo mr-1"></i>
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
              >
                submit
                <i className="fas fa-sign-in-alt ml-1"></i>
              </button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default TransactionModal
